import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { createHttpContext } from '@app/core/http/http-context';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { __ } from '../functions/object.functions';
import { Response } from './local/Response';

export class EntityService<T, TCreate, TUpdate> {
  protected readonly httpClient = inject(HttpClient);

  constructor(private readonly endpoint: string) {}

  getAll(
    queryParameters: string = '',
    skip: number = 0,
    take: number = 5,
    enableCache: boolean = false,
  ): Observable<T[]> {
    const _skip = __.IsNullOrUndefined(skip) ? 0 : skip;
    const _take = __.IsNullOrUndefined(take) ? 5 : take;
    const _enableCache = __.IsNullOrUndefined(enableCache) ? false : enableCache;
    const _queryParameters = __.IsNullOrUndefinedOrEmpty(queryParameters) ? '' : `&${queryParameters}`;
    const context = createHttpContext(_enableCache ? { cache: true } : { clearCache: true });

    return this.httpClient
      .get<Response<T[]>>(`${this.endpoint}?skip=${_skip}&take=${_take}${_queryParameters}`, { context })
      .pipe(map((response: Response<T[]>) => response.data));
  }

  /* Used to retrieve child */
  getAllChildren<TChild>(
    id: string,
    childEndpoint: string,
    queryParameters: string = '',
    skip: number = 0,
    take: number = 5,
    enableCache: boolean = false,
  ): Observable<TChild[]> {
    const _skip = __.IsNullOrUndefined(skip) ? 0 : skip;
    const _take = __.IsNullOrUndefined(take) ? 5 : take;
    const _enableCache = __.IsNullOrUndefined(enableCache) ? false : enableCache;
    const _queryParameters = __.IsNullOrUndefinedOrEmpty(queryParameters) ? '' : `&${queryParameters}`;
    const context = createHttpContext({
      skipErrorHandler: true,
      ...(_enableCache ? { cache: true } : { clearCache: true }),
    });

    return this.httpClient
      .get<Response<TChild[]>>(`${this.endpoint}/${id}/${childEndpoint}?skip=${_skip}&take=${_take}${_queryParameters}`, {
        context,
      })
      .pipe(map((response: Response<TChild[]>) => response.data));
  }

  getById(id: string): Observable<T> {
    return this.httpClient
      .get<Response<T>>(`${this.endpoint}/${id}`)
      .pipe(map((response: Response<T>) => response.data));
  }

  create(entity: TCreate): Observable<T> {
    return this.httpClient
      .post<Response<T>>(`${this.endpoint}`, entity)
      .pipe(map((response: Response<T>) => response.data));
  }

  update(id: string, entity: TUpdate): Observable<T> {
    return this.httpClient
      .patch<Response<T>>(`${this.endpoint}/${id}`, entity)
      .pipe(map((response: Response<T>) => response.data));
  }

  deleteById(id: string): Observable<boolean> {
    return this.httpClient.delete<Response<boolean>>(`${this.endpoint}/${id}`).pipe(
      map((response: Response<boolean>) => response.code.statusCode === 200),
    );
  }
}
